/**
 * Socket Context - Shared WebSocket context for all modules
 *
 * Features:
 * - Auto-connect when user is authenticated
 * - Auto-reconnect on connection loss
 * - Room subscription management
 * - Event listener registration
 *
 * Usage:
 * <SocketProvider socketUrl="http://localhost:5004">
 *   <App />
 * </SocketProvider>
 */

import React, { createContext, useContext, useState, useEffect, useCallback, useRef, ReactNode } from 'react';
import { io, Socket } from 'socket.io-client';
import { useAuth } from './AuthContext';

// Event types for type safety
export interface OcrStatusEvent {
    document_id: string;
    status: 'processing' | 'completed' | 'failed';
    progress: number;
    data?: any;
    error?: string;
    timestamp: string;
}

export interface MatchProgressEvent {
    job_id: string;
    current: number;
    total: number;
    percent: number;
    status: 'processing' | 'completed' | 'failed';
    result?: {
        invoice_id: string;
        quote_id: string;
        match_score: number;
        status: string;
    };
    timestamp: string;
}

export interface MatchCompleteEvent {
    job_id: string;
    status: 'completed';
    total_matches: number;
    summary: {
        matched?: number;
        discrepancy?: number;
        unmatched?: number;
    };
    timestamp: string;
}

export interface NotificationEvent {
    type: 'info' | 'success' | 'warning' | 'error';
    message: string;
    data?: any;
    timestamp: string;
}

type EventHandler<T = any> = (data: T) => void;

interface SocketContextType {
    isConnected: boolean;
    connectionError: string | null;
    subscribe: (type: 'document' | 'job' | 'company', id: string) => void;
    unsubscribe: (type: 'document' | 'job' | 'company', id: string) => void;
    on: <T = any>(event: string, handler: EventHandler<T>) => void;
    off: <T = any>(event: string, handler: EventHandler<T>) => void;
    emit: (event: string, data: any) => void;
}

interface SocketProviderProps {
    children: ReactNode;
    /** WebSocket server URL (e.g., http://localhost:5004) */
    socketUrl: string;
}

const SocketContext = createContext<SocketContextType | undefined>(undefined);

export const useSocket = (): SocketContextType => {
    const context = useContext(SocketContext);
    if (context === undefined) {
        throw new Error('useSocket must be used within a SocketProvider');
    }
    return context;
};

export const SocketProvider: React.FC<SocketProviderProps> = ({ children, socketUrl }) => {
    const { token, user, isAuthenticated } = useAuth();
    const [isConnected, setIsConnected] = useState(false);
    const [connectionError, setConnectionError] = useState<string | null>(null);

    const socketRef = useRef<Socket | null>(null);
    const subscriptionsRef = useRef<Set<string>>(new Set());

    useEffect(() => {
        if (!isAuthenticated || !token) {
            if (socketRef.current) {
                socketRef.current.disconnect();
                socketRef.current = null;
                setIsConnected(false);
            }
            return;
        }

        socketRef.current = io(socketUrl, {
            transports: ['websocket', 'polling'],
            autoConnect: true,
            reconnection: true,
            reconnectionAttempts: 10,
            reconnectionDelay: 1000,
            reconnectionDelayMax: 5000,
            auth: { token }
        });

        const socket = socketRef.current;

        socket.on('connect', () => {
            console.log('WebSocket connected:', socket.id);
            setIsConnected(true);
            setConnectionError(null);

            // Re-subscribe to all rooms after reconnection
            subscriptionsRef.current.forEach(subscription => {
                const [type, id] = subscription.split(':');
                socket.emit(`join_${type}`, { [`${type}_id`]: id });
            });

            if (user?.company_id) {
                socket.emit('join_company', { company_id: user.company_id });
            }
        });

        socket.on('disconnect', (reason) => {
            console.log('WebSocket disconnected:', reason);
            setIsConnected(false);
            if (reason === 'io server disconnect') {
                socket.connect();
            }
        });

        socket.on('connect_error', (error) => {
            console.error('WebSocket connection error:', error.message);
            setConnectionError(error.message);
            setIsConnected(false);
        });

        return () => {
            socket.disconnect();
            socketRef.current = null;
            setIsConnected(false);
        };
    }, [isAuthenticated, token, user?.company_id, socketUrl]);

    const subscribe = useCallback((type: 'document' | 'job' | 'company', id: string) => {
        if (!socketRef.current || !id) return;
        const subscription = `${type}:${id}`;
        if (subscriptionsRef.current.has(subscription)) return;
        subscriptionsRef.current.add(subscription);
        socketRef.current.emit(`join_${type}`, { [`${type}_id`]: id });
    }, []);

    const unsubscribe = useCallback((type: 'document' | 'job' | 'company', id: string) => {
        if (!socketRef.current || !id) return;
        const subscription = `${type}:${id}`;
        subscriptionsRef.current.delete(subscription);
        socketRef.current.emit(`leave_${type}`, { [`${type}_id`]: id });
    }, []);

    const on = useCallback(<T = any>(event: string, handler: EventHandler<T>) => {
        if (!socketRef.current) return;
        socketRef.current.on(event, handler);
    }, []);

    const off = useCallback(<T = any>(event: string, handler: EventHandler<T>) => {
        if (!socketRef.current) return;
        socketRef.current.off(event, handler);
    }, []);

    const emit = useCallback((event: string, data: any) => {
        if (!socketRef.current) return;
        socketRef.current.emit(event, data);
    }, []);

    const value: SocketContextType = {
        isConnected,
        connectionError,
        subscribe,
        unsubscribe,
        on,
        off,
        emit
    };

    return (
        <SocketContext.Provider value={value}>
            {children}
        </SocketContext.Provider>
    );
};

export default SocketProvider;
